import { sqliteTable, text, integer, real, blob, index, uniqueIndex, primaryKey } from 'drizzle-orm/sqlite-core';
import { generateId } from '@/lib/id';

// ─── 1. Accounts ───────────────────────────────────────────────

export const accounts = sqliteTable('accounts', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull(),
    type: text('type').notNull(), // checking, savings, wallet, digital, investment, other
    institution: text('institution'),
    institutionIcon: text('institution_icon').default('default'),
    initialBalance: real('initial_balance').notNull().default(0),
    currentBalance: real('current_balance').notNull().default(0),
    color: text('color').notNull().default('#2B697A'),
    icon: text('icon').notNull().default('wallet'),
    isArchived: integer('is_archived').notNull().default(0),
    isExcludedFromTotals: integer('is_excluded_from_totals').notNull().default(0),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_accounts_type').on(table.type),
    index('idx_accounts_archived').on(table.isArchived),
    index('idx_accounts_visible').on(table.deletedAt, table.isArchived, table.sortOrder),
]);

// ─── 2. Credit Cards ──────────────────────────────────────────

export const creditCards = sqliteTable('credit_cards', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull(),
    brand: text('brand'), // visa, mastercard, elo, amex, hipercard, other
    institution: text('institution'),
    institutionIcon: text('institution_icon').default('default'),
    lastFourDigits: text('last_four_digits'),
    creditLimit: real('credit_limit').notNull().default(0),
    closingDay: integer('closing_day').notNull(),
    dueDay: integer('due_day').notNull(),
    // Vencimento em dias úteis (ADR 0082): rola a data ÚTIL derivada nas superfícies de
    // pagamento; invoices.due_date segue sempre nominal. Por cartão porque os bancos divergem.
    dueOnBusinessDay: integer('due_on_business_day').notNull().default(0),
    // Compra no DIA do fechamento vai pra fatura seguinte (ADR 0130): a janela termina na
    // véspera do fechamento cadastrado. Por cartão porque os bancos divergem; só vale pra
    // lançamento novo — o invoice_id gravado nunca é recalculado.
    closingDayToNextInvoice: integer('closing_day_to_next_invoice').notNull().default(0),
    paymentAccountId: text('payment_account_id').references(() => accounts.id),
    color: text('color').notNull().default('#E8927C'),
    icon: text('icon').notNull().default('credit-card'),
    isArchived: integer('is_archived').notNull().default(0),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_credit_cards_archived').on(table.isArchived),
    index('idx_credit_cards_visible').on(table.deletedAt, table.isArchived),
]);

// ─── 3. Invoices ──────────────────────────────────────────────

export const invoices = sqliteTable('invoices', {
    id: text('id').primaryKey().$defaultFn(generateId),
    creditCardId: text('credit_card_id').notNull().references(() => creditCards.id),
    referenceMonth: text('reference_month').notNull(), // YYYY-MM
    openingDate: text('opening_date').notNull(),
    closingDate: text('closing_date').notNull(),
    dueDate: text('due_date').notNull(),
    totalAmount: real('total_amount').notNull().default(0),
    manualTotal: real('manual_total'),
    paidAmount: real('paid_amount').notNull().default(0),
    remainderAmount: real('remainder_amount').notNull().default(0),
    remainderAction: text('remainder_action'), // rollover, absorb
    rolledFromInvoiceId: text('rolled_from_invoice_id').references((): any => invoices.id),
    status: text('status').notNull().default('open'), // open, closed, paid, partial, overdue
    isManuallySet: integer('is_manually_set').notNull().default(0),
    // Selo "saúde da fatura": registra quando o usuário conferiu a fatura contra o
    // PDF do banco (reconciliação/importação). Nulo = nunca conferida (sem selo).
    // É um fato pontual, não streak: edições posteriores não o invalidam.
    reconciledAt: text('reconciled_at'), // ISO timestamp da última conferência com o banco
    reconciledItemCount: integer('reconciled_item_count'), // nº de itens conferidos contra o banco naquela conferência
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    uniqueIndex('idx_invoices_card_month').on(table.creditCardId, table.referenceMonth),
    index('idx_invoices_status').on(table.status),
    index('idx_invoices_due_date').on(table.dueDate),
    index('idx_invoices_due_card').on(table.dueDate, table.creditCardId),
    index('idx_invoices_reference_month').on(table.referenceMonth),
]);

// ─── 3.1 Invoice Payments ─────────────────────────────────────

export const invoicePayments = sqliteTable('invoice_payments', {
    id: text('id').primaryKey().$defaultFn(generateId),
    invoiceId: text('invoice_id').notNull().references(() => invoices.id),
    accountId: text('account_id').notNull().references(() => accounts.id),
    amount: real('amount').notNull(),
    date: text('date').notNull(),
    notes: text('notes'),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_invoice_payments_invoice').on(table.invoiceId),
]);

// ─── 4. Categories ────────────────────────────────────────────

export const categories = sqliteTable('categories', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull(),
    type: text('type').notNull(), // expense, income
    icon: text('icon').notNull().default('folder'),
    color: text('color').notNull().default('#2B697A'),
    isSystem: integer('is_system').notNull().default(0),
    isArchived: integer('is_archived').notNull().default(0),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_categories_type').on(table.type),
    index('idx_categories_visible').on(table.deletedAt, table.type, table.isArchived),
]);

// ─── 5. Subcategories ─────────────────────────────────────────

export const subcategories = sqliteTable('subcategories', {
    id: text('id').primaryKey().$defaultFn(generateId),
    categoryId: text('category_id').notNull().references(() => categories.id),
    name: text('name').notNull(),
    icon: text('icon'),
    isSystem: integer('is_system').notNull().default(0),
    isArchived: integer('is_archived').notNull().default(0),
    sortOrder: integer('sort_order').notNull().default(0),
    // Identidade de origem no seed (nome canônico do categories*.json), preservada em
    // rename/soft-delete. NULL = criada pelo usuário ou renomeada antes da migração 0031.
    seedName: text('seed_name'),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_subcategories_category').on(table.categoryId),
]);

// ─── 6. Transactions ──────────────────────────────────────────

export const transactions = sqliteTable('transactions', {
    id: text('id').primaryKey().$defaultFn(generateId),
    type: text('type').notNull(), // income, expense, transfer, adjustment
    amount: real('amount').notNull(),
    description: text('description').notNull(),
    date: text('date').notNull(), // YYYY-MM-DD
    time: text('time'), // HH:MM
    notes: text('notes'),

    // Source/destination
    accountId: text('account_id').references(() => accounts.id),
    creditCardId: text('credit_card_id').references(() => creditCards.id),
    invoiceId: text('invoice_id').references(() => invoices.id),

    // Transfers
    transferDestinationAccountId: text('transfer_destination_account_id').references(() => accounts.id),
    transferId: text('transfer_id'),

    // Categorization
    categoryId: text('category_id').references(() => categories.id),
    subcategoryId: text('subcategory_id').references(() => subcategories.id),

    // Recurring
    isRecurring: integer('is_recurring').notNull().default(0),
    recurringId: text('recurring_id'),
    // Data AGENDADA da ocorrência (slot da régua). Fica fixa mesmo que a transação
    // seja paga/movida pra outra data (antecipação/adiamento) — é por ela que a
    // projeção deduplica, pra não re-emitir uma ocorrência já materializada.
    recurringOccurrenceDate: text('recurring_occurrence_date'),

    // Installments
    installmentId: text('installment_id'),
    installmentNumber: integer('installment_number'),
    installmentTotal: integer('installment_total'),

    // Financings
    financingId: text('financing_id').references(() => financings.id),
    financingEntryType: text('financing_entry_type'),
    financingInstallmentNumber: integer('financing_installment_number'),

    // Personal loans ("Quem me deve")
    personalLoanId: text('personal_loan_id').references(() => personalLoans.id),
    personalLoanEntryType: text('personal_loan_entry_type'),

    // Adjustments
    adjustmentType: text('adjustment_type'), // account_balance, invoice_manual_entry, invoice_remainder

    isConfirmed: integer('is_confirmed').notNull().default(1),

    // Subscriptions screen
    isHiddenFromSubs: integer('is_hidden_from_subs').notNull().default(0),

    // Lançamento ignorado (ADR 0037): sai de toda leitura analítica (competência, caixa
    // e Saldo do período) por vontade do usuário, mas SEGUE no saldo real da conta e na
    // fatura do cartão. Predicado canônico em db/repositories/transactionScopes.ts.
    isIgnored: integer('is_ignored').notNull().default(0),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_transactions_date').on(table.date),
    index('idx_transactions_type').on(table.type),
    index('idx_transactions_account').on(table.accountId),
    index('idx_transactions_credit_card').on(table.creditCardId),
    index('idx_transactions_invoice').on(table.invoiceId),
    index('idx_transactions_category').on(table.categoryId),
    index('idx_transactions_recurring').on(table.recurringId),
    index('idx_transactions_installment').on(table.installmentId),
    index('idx_transactions_financing').on(table.financingId),
    index('idx_transactions_personal_loan').on(table.personalLoanId),
    index('idx_transactions_deleted').on(table.deletedAt),
    index('idx_transactions_visible_confirmed_date').on(table.deletedAt, table.isConfirmed, table.date),
    index('idx_transactions_visible_type_date').on(table.deletedAt, table.type, table.date),
    index('idx_transactions_visible_invoice_date').on(table.deletedAt, table.invoiceId, table.date),
    index('idx_transactions_visible_account_date').on(table.deletedAt, table.accountId, table.date),
    index('idx_transactions_visible_transfer_dest_date').on(table.deletedAt, table.transferDestinationAccountId, table.date),
    index('idx_transactions_spending').on(table.deletedAt, table.type, table.date, table.categoryId),
]);

// ─── 7. Tags ──────────────────────────────────────────────────

export const tags = sqliteTable('tags', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull().unique(),
    color: text('color').notNull().default('#6B7280'),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
});

export const transactionTags = sqliteTable('transaction_tags', {
    transactionId: text('transaction_id').notNull().references(() => transactions.id),
    tagId: text('tag_id').notNull().references(() => tags.id),
}, (table) => [
    // O par (transação, tag) é a identidade da linha — junction é set-membership, sem
    // campo mutável. O índice único dá a identidade estável que o sync exige e barra
    // pares duplicados (ver docs/adr/0030, migração 0019). Substitui uma PK composta
    // (que em SQLite exigiria rebuild de tabela com FK ligada).
    uniqueIndex('idx_transaction_tags_unique').on(table.transactionId, table.tagId),
    index('idx_transaction_tags_transaction').on(table.transactionId),
    index('idx_transaction_tags_tag').on(table.tagId),
]);

// ─── 8. Recurring Transactions ────────────────────────────────

export const recurringTransactions = sqliteTable('recurring_transactions', {
    id: text('id').primaryKey().$defaultFn(generateId),
    type: text('type').notNull(), // income, expense, transfer
    amount: real('amount').notNull(),
    description: text('description').notNull(),
    // Observação e hora da SÉRIE: a regra é a fonte de verdade e cada ocorrência
    // materializada herda em transactions.notes/time (uma ocorrência pode divergir —
    // Exceção). Espelha o desenho das tags da regra (recurring_transaction_tags).
    // Ver CONTEXT.md ("Observação (numa recorrência)").
    notes: text('notes'),
    time: text('time'), // HH:MM


    accountId: text('account_id').references(() => accounts.id),
    creditCardId: text('credit_card_id').references(() => creditCards.id),
    categoryId: text('category_id').references(() => categories.id),
    subcategoryId: text('subcategory_id').references(() => subcategories.id),
    transferDestinationAccountId: text('transfer_destination_account_id').references(() => accounts.id),

    frequency: text('frequency').notNull(), // unidade base: daily, weekly, monthly, annual (ver docs/adr/0002)
    interval: integer('interval').notNull().default(1), // "a cada N unidades" (quinzenal = weekly+2)
    weekdays: text('weekdays'), // semanal multi-dia (BYDAY), ex.: "2,4" = ter+qui (getDay: 0=dom)
    monthlyMode: text('monthly_mode'), // mensal: null/'day_of_month' (dia do mês) ou 'nth_weekday' (ex.: 1º sábado)
    startDate: text('start_date').notNull(),
    endDate: text('end_date'),
    count: integer('count'), // termina "após N ocorrências"; intenção de autoria, deriva endDate
    nextOccurrence: text('next_occurrence').notNull(),
    isActive: integer('is_active').notNull().default(1),
    isHiddenFromSubs: integer('is_hidden_from_subs').notNull().default(0),
    // Lançamento ignorado da SÉRIE (ADR 0169, que estende o 0037 à recorrência): a regra é a
    // fonte de verdade e cada ocorrência — materializada (transactions.is_ignored) E projetada —
    // herda o flag. Fora de toda leitura analítica; dentro do saldo da conta e da fatura. Uma
    // ocorrência pode divergir (Exceção), como a nota e a tag.
    isIgnored: integer('is_ignored').notNull().default(0),
    // Aviso antecipado (Pro): lembrete individual 1 dia antes da ocorrência. Opt-in por regra,
    // nasce ligado em despesa da categoria Assinaturas (derivado no create). DISPARAR exige Pro;
    // o flag pode estar ligado num não-Pro mas inerte (gancho de conversão). Ver docs/adr/0025.
    advanceReminderEnabled: integer('advance_reminder_enabled').notNull().default(0),
    skippedDates: text('skipped_dates'), // Exceção por exclusão: datas (JSON ["yyyy-MM-dd"]) puladas sem afetar o resto da série (ver docs/adr/0014)

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_recurring_next').on(table.nextOccurrence),
    index('idx_recurring_active').on(table.isActive),
    index('idx_recurring_active_window').on(
        table.isActive,
        table.deletedAt,
        table.nextOccurrence,
        table.endDate,
    ),
    index('idx_recurring_subs').on(
        table.type,
        table.isActive,
        table.isHiddenFromSubs,
        table.deletedAt,
    ),
]);

// Tags da REGRA recorrente (série). Espelha transaction_tags: a regra é a fonte de verdade
// das tags da série e cada ocorrência materializada herda essas tags na própria
// transaction_tags. Uma ocorrência pode divergir (Exceção). Ver CONTEXT.md ("Tag numa recorrência").
export const recurringTransactionTags = sqliteTable('recurring_transaction_tags', {
    recurringId: text('recurring_id').notNull().references(() => recurringTransactions.id),
    tagId: text('tag_id').notNull().references(() => tags.id),
}, (table) => [
    // Mesma identidade set-membership de transaction_tags: o par (regra, tag) é a linha.
    // Índice único = identidade estável p/ sync + barreira anti-duplicata (ver migração 0019).
    uniqueIndex('idx_recurring_transaction_tags_unique').on(table.recurringId, table.tagId),
    index('idx_recurring_transaction_tags_recurring').on(table.recurringId),
    index('idx_recurring_transaction_tags_tag').on(table.tagId),
]);

// ─── 9. Installments ─────────────────────────────────────────

export const installments = sqliteTable('installments', {
    id: text('id').primaryKey().$defaultFn(generateId),
    description: text('description').notNull(),
    totalAmount: real('total_amount').notNull(),
    installmentCount: integer('installment_count').notNull(),
    installmentAmount: real('installment_amount').notNull(),
    creditCardId: text('credit_card_id').notNull().references(() => creditCards.id),
    categoryId: text('category_id').references(() => categories.id),
    subcategoryId: text('subcategory_id').references(() => subcategories.id),
    startDate: text('start_date').notNull(),
    isActive: integer('is_active').notNull().default(1),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_installments_card').on(table.creditCardId),
    index('idx_installments_active').on(table.isActive),
]);

// ─── 10. Budgets ─────────────────────────────────────────────

export const financings = sqliteTable('financings', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull(),
    // 'financing' | 'loan' — muda só o rótulo e a copy da tela; o modelo (contrato +
    // série de parcelas + abatimentos) é o mesmo para os dois. Ver ADR 0055.
    kind: text('kind').notNull().default('financing'),
    institution: text('institution'),
    paymentAccountId: text('payment_account_id').notNull().references(() => accounts.id),
    categoryId: text('category_id').notNull().references(() => categories.id),
    subcategoryId: text('subcategory_id').references(() => subcategories.id),
    monthlyAmount: real('monthly_amount').notNull(),
    totalInstallments: integer('total_installments').notNull(),
    // Parâmetros da Curva da parcela (ADR 0081). 'fixed' é o padrão e significa "o banco
    // não disse" — é o que todo contrato anterior à feature é; NÃO é Price. A curva não é
    // materializada: o valor de contrato de cada parcela é função pura destes campos
    // (`lib/financingCurve.ts`), o que preserva a invariante da ADR 0055.
    amortizationSystem: text('amortization_system').notNull().default('fixed'),
    /** Taxa do contrato, AO ANO e em pontos percentuais (8.99 = 8,99% a.a.). */
    annualRate: real('annual_rate'),
    /** Saldo devedor base da derivação, válido ANTES de `curveInstallmentNumber`. */
    curveBalance: real('curve_balance'),
    curveInstallmentNumber: integer('curve_installment_number'),
    /** Seguros e taxas: adicional fixo que ancora a curva no boleto real. */
    curveExtraAmount: real('curve_extra_amount'),
    nextInstallmentNumber: integer('next_installment_number'),
    nextDueDate: text('next_due_date'),
    lastKnownBalance: real('last_known_balance'),
    balanceUpdatedAt: text('balance_updated_at'),
    status: text('status').notNull().default('active'),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_financings_status').on(table.status),
    index('idx_financings_account').on(table.paymentAccountId),
    index('idx_financings_category').on(table.categoryId),
]);

export const financingAdjustments = sqliteTable('financing_adjustments', {
    id: text('id').primaryKey().$defaultFn(generateId),
    financingId: text('financing_id').notNull().references(() => financings.id),
    date: text('date').notNull(),
    amount: real('amount').notNull(),
    newOutstandingBalance: real('new_outstanding_balance'),
    remainingInstallments: integer('remaining_installments').notNull(),
    notes: text('notes'),
    transactionId: text('transaction_id').references(() => transactions.id),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_financing_adjustments_financing').on(table.financingId),
    index('idx_financing_adjustments_transaction').on(table.transactionId),
    index('idx_financing_adjustments_date').on(table.date),
]);

// ─── 10.1 Personal Loans ("Quem me deve") ────────────────────

// Pessoa (devedor): entidade leve estilo tag — criada inline com autocomplete,
// renomeável. Não é um cadastro de contatos.
export const people = sqliteTable('people', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull().unique(),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
});

// Empréstimo a pessoa: recebível (troca de ativo). O estado (saldo em aberto,
// data combinada, status) vive aqui, não nas transações vinculadas — ver ADR 0003.
export const personalLoans = sqliteTable('personal_loans', {
    id: text('id').primaryKey().$defaultFn(generateId),
    personId: text('person_id').notNull().references(() => people.id),
    // Conta da saída (e padrão dos recebimentos). Null em "empréstimo já
    // existente" — a saída aconteceu antes do app e não debita conta nenhuma.
    accountId: text('account_id').references(() => accounts.id),
    amount: real('amount').notNull(),
    outstandingBalance: real('outstanding_balance').notNull(),
    date: text('date').notNull(), // YYYY-MM-DD
    // Data combinada vigente (no máx. 1, renegociável). Null = "quando der".
    agreedDate: text('agreed_date'),
    // Cronograma pretendido (ADR 0036): JSON [{date, amount}] com 2+ parcelas — a fonte
    // DURÁVEL do plano. A cauda em aberto (scheduled_receipt) é derivada dele menos o que
    // já entrou (FIFO); por isso desfazer um recebimento restaura as parcelas antigas. Null
    // = data única / "quando der" / legado (a cauda vive só nas linhas abertas).
    schedule: text('schedule'),
    status: text('status').notNull().default('open'), // open, settled, written_off
    notes: text('notes'),
    // Nasceu com saldo em aberto, sem transação de saída.
    isExisting: integer('is_existing').notNull().default(0),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_personal_loans_person').on(table.personId),
    index('idx_personal_loans_status').on(table.status),
    index('idx_personal_loans_account').on(table.accountId),
]);

// Eventos do empréstimo: recebimento, baixa e renegociação de data combinada.
// Espelha financing_adjustments — os eventos são o razão; o empréstimo guarda
// o snapshot derivado deles.
export const personalLoanEvents = sqliteTable('personal_loan_events', {
    id: text('id').primaryKey().$defaultFn(generateId),
    loanId: text('loan_id').notNull().references(() => personalLoans.id),
    type: text('type').notNull(), // receipt, write_off, reschedule
    date: text('date').notNull(),
    // Quanto abateu o saldo (principal do recebimento ou valor da baixa; 0 em reschedule).
    amount: real('amount').notNull().default(0),
    // Parte do recebimento que passou do saldo — vira receita.
    excessAmount: real('excess_amount'),
    newOutstandingBalance: real('new_outstanding_balance').notNull(),
    // Reschedule: a nova data combinada (null = removida).
    agreedDate: text('agreed_date'),
    notes: text('notes'),
    transactionId: text('transaction_id').references(() => transactions.id),
    excessTransactionId: text('excess_transaction_id').references(() => transactions.id),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_personal_loan_events_loan').on(table.loanId),
    index('idx_personal_loan_events_date').on(table.date),
    index('idx_personal_loan_events_transaction').on(table.transactionId),
]);

// ─── 10. Budgets ─────────────────────────────────────────────

export const budgets = sqliteTable('budgets', {
    id: text('id').primaryKey().$defaultFn(generateId),
    // `categoryId` continua como "categoria principal" só por compatibilidade
    // (NOT NULL + consumidores legados). A verdade do conjunto de categorias é a
    // tabela `budget_categories`.
    categoryId: text('category_id').notNull().references(() => categories.id),
    // 'expense' = teto de gasto (orçamento clássico); 'income' = meta de receita
    // ("faturamento do mês") — mesmo shape, o realizado soma income em vez de
    // expense. Um conceito só na UI (ADR 0046).
    type: text('type').notNull().default('expense'),
    // Identidade própria do orçamento (necessária quando cobre N categorias).
    name: text('name').notNull().default(''),
    icon: text('icon').notNull().default('wallet'),
    color: text('color').notNull().default('#2B697A'),
    amount: real('amount').notNull(),
    // Cadência do Ciclo do orçamento (ADR 0120): 'monthly' | 'biweekly' | 'weekly'. Até a
    // migração 0040 só o relatório lia isto (×52/12) e as telas comparavam o teto semanal
    // cru contra o mês inteiro. Agora o ciclo é de verdade — ver lib/budgetCycle.ts.
    period: text('period').notNull().default('monthly'),
    // Dia de início do ciclo MENSAL (1–31; 1 = mês-calendário). Ignorado nas outras cadências.
    cycleStartDay: integer('cycle_start_day').notNull().default(1),
    // Data de referência ('yyyy-MM-dd') do ciclo SEMANAL/QUINZENAL: começa UM ciclo qualquer,
    // os outros se contam dela de 7/14 em 7/14 dias. Nula no mensal. Nula num semanal
    // (backup antigo) cai em DEFAULT_CYCLE_ANCHOR_DATE.
    cycleAnchorDate: text('cycle_anchor_date'),
    // INERTE. O switch "Acumular saldo" foi REMOVIDO do formulário em 29/08/2026: ele
    // gravava aqui e nenhum código lia — a pessoa ligava e não acontecia nada. A coluna
    // fica (sem migration: dropar em SQLite é rebuild de tabela, e o sync captura a
    // coluna), presa em 0 pelo default. NÃO religar o switch sem implementar o rollover
    // de verdade, que não é ajuste e sim feature: o cálculo é por janela isolada e não há
    // histórico de orçamento — mudar o teto vale retroativamente, então o saldo acumulado
    // mudaria sozinho a cada edição. Exige congelar teto e sobra por período fechado.
    rollover: integer('rollover').notNull().default(0),
    // INERTE desde 18/09/2026. Era gravado pelo formulário (sempre 0.8, sem controle na tela)
    // e NUNCA lido: não existe notificação de orçamento no app. Form, zod, `BudgetInput` e as
    // escritas foram arrancados (regra: config sem tela que a escreva não existe). A coluna
    // fica pelo mesmo motivo do rollover — dropar em SQLite é rebuild de tabela e o sync
    // captura a coluna — presa em 0.8 pelo default. Alerta de orçamento, se um dia vier, é
    // FEATURE (tela + leitura + ADR), não é religar a chave. Guarda que fica vermelha se
    // alguém religar: lib/__tests__/deadConfig.test.ts.
    alertThreshold: real('alert_threshold').notNull().default(0.8),
    isActive: integer('is_active').notNull().default(1),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_budgets_active_category').on(table.isActive, table.categoryId),
]);

// Liga um orçamento a 1..N categorias (um orçamento com limite compartilhado).
// Exclusividade (uma categoria em no máximo um orçamento) é garantida na camada
// de repositório/UI, não por constraint, pra não quebrar a migração em bases
// antigas que por acaso tenham duplicatas.
export const budgetCategories = sqliteTable('budget_categories', {
    id: text('id').primaryKey().$defaultFn(generateId),
    budgetId: text('budget_id').notNull().references(() => budgets.id, { onDelete: 'cascade' }),
    categoryId: text('category_id').notNull().references(() => categories.id),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_budget_categories_budget').on(table.budgetId),
    index('idx_budget_categories_category').on(table.categoryId),
]);

// Limite por subcategoria (ADR 0067): recorte OPCIONAL dentro de um orçamento que já
// cobre a categoria da subcategoria — "Casa = R$ 1.000, SENDO Contas no máximo R$ 300".
// Não é um orçamento: não tem nome/ícone/cor, não conta no soft-limit do plano grátis e
// não entra em nenhuma soma de "quanto eu orcei". Pai e filho vigiam o mesmo gasto de
// propósito. O envelope residual ("Demais lançamentos") é CALCULADO (teto − soma dos
// limites), nunca gravado.
export const budgetSubcategoryLimits = sqliteTable('budget_subcategory_limits', {
    id: text('id').primaryKey().$defaultFn(generateId),
    budgetId: text('budget_id').notNull().references(() => budgets.id, { onDelete: 'cascade' }),
    subcategoryId: text('subcategory_id').notNull().references(() => subcategories.id),
    amount: real('amount').notNull(),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_budget_subcategory_limits_budget').on(table.budgetId),
    uniqueIndex('idx_budget_subcategory_limits_unique').on(table.budgetId, table.subcategoryId),
]);

// Origem do orçamento (ADR 0103): o subconjunto OPCIONAL de Contas/Cartões em que o
// orçamento vigia. Zero linhas = TODAS as origens = o orçamento clássico, que é o estado
// de toda base existente — por isso não há backfill.
// `accountId` e `creditCardId` são EXCLUDENTES, espelhando o lançamento (o formulário é um
// XOR): cada linha aponta uma origem só. A exclusividade é garantida na camada de
// repositório, como em `budget_categories`.
// ATENÇÃO: os índices de account/card não são só performance — `purgeSoftDeletes` consulta
// esta tabela por eles para não hard-deletar conta/cartão que um orçamento ainda aponta.
export const budgetOrigins = sqliteTable('budget_origins', {
    id: text('id').primaryKey().$defaultFn(generateId),
    budgetId: text('budget_id').notNull().references(() => budgets.id, { onDelete: 'cascade' }),
    accountId: text('account_id').references(() => accounts.id),
    creditCardId: text('credit_card_id').references(() => creditCards.id),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_budget_origins_budget').on(table.budgetId),
    index('idx_budget_origins_account').on(table.accountId),
    index('idx_budget_origins_card').on(table.creditCardId),
]);

// Orçamento por subcategoria (ADR 0110): o subconjunto OPCIONAL de Subcategorias em que o
// orçamento vigia, por categoria — "Alimentação, SÓ em Energético". Zero linhas para uma
// categoria = a categoria inteira (inclusive o gasto sem subcategoria), que é o orçamento
// clássico e o estado de toda base existente — por isso não há backfill. É INCLUSÃO, não
// exclusão: subcategoria criada depois NÃO entra sozinha, como um cartão novo não entra
// numa Origem. Diferente do Limite por subcategoria (que é teto DENTRO do orçamento), o
// recorte diz o que CONTA; os dois convivem: limite só faz sentido em subcategoria que conta.
// ATENÇÃO: o índice por subcategoria é usado pelo `purgeSoftDeletes`.
export const budgetSubcategories = sqliteTable('budget_subcategories', {
    id: text('id').primaryKey().$defaultFn(generateId),
    budgetId: text('budget_id').notNull().references(() => budgets.id, { onDelete: 'cascade' }),
    subcategoryId: text('subcategory_id').notNull().references(() => subcategories.id),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_budget_subcategories_budget').on(table.budgetId),
    index('idx_budget_subcategories_subcategory').on(table.subcategoryId),
    uniqueIndex('idx_budget_subcategories_unique').on(table.budgetId, table.subcategoryId),
]);

// ─── 12. Goals ───────────────────────────────────────────────

export const goals = sqliteTable('goals', {
    id: text('id').primaryKey().$defaultFn(generateId),
    name: text('name').notNull(),
    targetAmount: real('target_amount').notNull(),
    currentAmount: real('current_amount').notNull().default(0),
    deadline: text('deadline'),
    accountId: text('account_id').references(() => accounts.id),
    icon: text('icon').notNull().default('target'),
    color: text('color').notNull().default('#10B981'),
    isCompleted: integer('is_completed').notNull().default(0),
    completedAt: text('completed_at'),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_goals_visible').on(table.deletedAt, table.isCompleted),
]);

// ─── 13. Goal Deposits ──────────────────────────────────────

export const goalDeposits = sqliteTable('goal_deposits', {
    id: text('id').primaryKey().$defaultFn(generateId),
    goalId: text('goal_id').notNull().references(() => goals.id),
    amount: real('amount').notNull(),
    date: text('date').notNull(),
    notes: text('notes'),
    // Conta debitada (opcional, estilo caixinha) e a transação de ajuste que
    // registrou o desconto — usada para estornar ao remover o depósito.
    accountId: text('account_id').references(() => accounts.id),
    transactionId: text('transaction_id').references(() => transactions.id),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_goal_deposits_goal').on(table.goalId),
]);

// ─── 13b. Reservas (caixinhas) ───────────────────────────────
// Dinheiro reservado DENTRO de uma conta (espelha a "Caixinha" do Nubank/PicPay/C6).
// É uma camada de earmark: abate o Disponível da conta, mas NÃO cria transação nem
// muda o currentBalance da conta. Ver ADR 0015 e CONTEXT.md (Reserva, Disponível).

export const reservas = sqliteTable('reservas', {
    id: text('id').primaryKey().$defaultFn(generateId),
    accountId: text('account_id').notNull().references(() => accounts.id),
    name: text('name').notNull(),
    currentAmount: real('current_amount').notNull().default(0),
    targetAmount: real('target_amount'), // opcional: reserva pode não ter meta
    deadline: text('deadline'),
    icon: text('icon').notNull().default('piggy-bank'),
    color: text('color').notNull().default('#10B981'),
    // Reserva turbinada (fast-follow): quando preenchido, soma ao limite do cartão.
    creditCardId: text('credit_card_id').references(() => creditCards.id),
    // Marcador opt-in (ADR 0022): quando 1, a caixinha conta no sub-score "Reserva" da
    // saúde financeira (junto com contas poupança/investimento), tenha alvo ou não.
    isEmergencyFund: integer('is_emergency_fund').notNull().default(0),
    isArchived: integer('is_archived').notNull().default(0),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_reservas_account').on(table.accountId, table.deletedAt),
]);

// ─── 13c. Reserva Movements (guardar/resgatar) ───────────────
// Movimento NEUTRO entre Disponível e Reservado na mesma conta. Não é transação:
// não vira receita/despesa nem altera o patrimônio.

export const reservaMovements = sqliteTable('reserva_movements', {
    id: text('id').primaryKey().$defaultFn(generateId),
    reservaId: text('reserva_id').notNull().references(() => reservas.id),
    type: text('type').notNull(), // 'deposit' (guardar) | 'withdraw' (resgatar) | 'yield' (rendimento)
    amount: real('amount').notNull(), // sempre positivo
    date: text('date').notNull(),
    notes: text('notes'),
    // Só no 'yield': a receita que o rendimento creditou na conta (ADR 0161). Os dois nascem e
    // morrem juntos — a receita se desfaz pela caixinha. Sem FK (a receita é soft-delete; a purga
    // zera a referência antes de apagar).
    transactionId: text('transaction_id'),
    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_reserva_movements_reserva').on(table.reservaId),
    index('idx_reserva_movements_transaction').on(table.transactionId),
]);

// ─── 14. Settings ────────────────────────────────────────────

export const settings = sqliteTable('settings', {
    key: text('key').primaryKey(),
    value: text('value').notNull(),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
});

// ─── 15. Pending Notifications ───────────────────────────────

export const pendingNotifications = sqliteTable('pending_notifications', {
    id: text('id').primaryKey().$defaultFn(generateId),
    bankId: text('bank_id').notNull(), // santander, mercado_pago, nubank, itau, banestes
    transactionType: text('transaction_type').notNull(), // card_purchase, pix_sent, pix_received
    amount: real('amount').notNull(),
    description: text('description').notNull(),
    cardLastDigits: text('card_last_digits'),
    date: text('date').notNull(), // YYYY-MM-DD
    time: text('time'), // HH:MM

    rawText: text('raw_text').notNull(),
    rawPackage: text('raw_package').notNull(),
    dedupeKey: text('dedupe_key').notNull(),
    // Quem emitiu o cartão, quando a FONTE não é ele: a carteira digital mostra o nome do
    // cartão ("Mercado Pago Mastercard Crédito") e é só por aí que a pendente acha a conta
    // certa e se pareia com a notificação do banco. NULL em toda fonte que não é carteira
    // (ADR 0096). Texto cru, como veio — quem resolve a instituição é lib/notificationCrossSource.
    issuerHint: text('issuer_hint'),

    // Captura externa assistida (ADR 0115). Tudo nullable para as linhas antigas e para
    // notificações Android: `raw_package` continua sendo compatibilidade, enquanto estes
    // campos carregam o contrato explícito da inbox iOS.
    sourceKind: text('source_kind'), // ios_wallet | ios_screen | ios_quick_manual | ios_bank_notification
    sourceCaptureId: text('source_capture_id'), // UUID estável do envelope/App Intent
    sourceCurrencyCode: text('source_currency_code'), // ISO 4217 recebido, antes de converter
    suggestedCategoryId: text('suggested_category_id'), // snapshot pode estar velho; sem FK de propósito

    status: text('status').notNull().default('pending'), // pending, confirmed, dismissed
    createdTransactionId: text('created_transaction_id').references(() => transactions.id),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    index('idx_pending_notifications_status').on(table.status),
    index('idx_pending_notifications_date').on(table.date),
    index('idx_pending_notifications_dedup').on(table.bankId, table.date, table.amount),
    // NÃO-único de propósito (ADR 0038): a dedup é por JANELA DE TEMPO no repositório, não
    // por chave imortal. Índice só pra achar rápido a captura recente da mesma chave.
    index('idx_pending_notifications_dedupe_key').on(table.dedupeKey),
    // NULL é permitido em várias linhas; UUID preenchido é globalmente único e torna
    // a drenagem da inbox idempotente mesmo depois da janela de 5 min da ADR 0038.
    uniqueIndex('idx_pending_notifications_source_capture').on(table.sourceCaptureId),
    // O pareamento carteira ↔ emissor (ADR 0096) varre as capturas dos últimos minutos, e
    // é por created_at que ele acha — não pela data da compra, que os dois lados escrevem
    // com liberdade. Sem este índice a varredura passa na tabela inteira, que só cresce.
    index('idx_pending_notifications_created_at').on(table.createdAt),
]);

// ─── 16. Auto-Categorization Rules ──────────────────────────

export const autoCategorizationRules = sqliteTable('auto_categorization_rules', {
    id: text('id').primaryKey().$defaultFn(generateId),
    matchPattern: text('match_pattern').notNull(),
    categoryId: text('category_id').notNull().references(() => categories.id),
    subcategoryId: text('subcategory_id').references(() => subcategories.id),
    priority: integer('priority').notNull().default(0),
    isActive: integer('is_active').notNull().default(1),

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    // Soft-delete: a linha vira "tombstone" em vez de sumir. Necessário para o
    // seeder (ensureDefaultAutoRules) não ressuscitar uma regra padrão apagada.
    deletedAt: text('deleted_at'),
}, (table) => [
    index('idx_auto_rules_active').on(table.isActive, table.priority),
]);

// ─── 16b. Sync (interno — NÃO sincroniza, NÃO entra no backup) ──
// Outbox da captura de escrita por triggers (ver lib/sync/capture.ts e docs/adr/0031).
// Os triggers só existem com sync ligado; as tabelas existem sempre (vazias, custo zero).

export const syncOutbox = sqliteTable('sync_outbox', {
    seq: integer('seq').primaryKey({ autoIncrement: true }),
    tableName: text('table_name').notNull(),
    rowId: text('row_id').notNull(),
    columnName: text('column_name').notNull(),
    // Sem afinidade de tipo forte: guarda o valor como veio (número fica número, texto fica texto).
    value: blob('value'),
}, (table) => [
    index('idx_sync_outbox_cell').on(table.tableName, table.rowId, table.columnName),
]);

// Guard anti-eco: com uma linha aqui, os triggers de captura ficam mudos (aplicação de
// mensagens remotas / restore de bootstrap não re-emitem como escrita local).
export const syncGuard = sqliteTable('sync_guard', {
    id: integer('id').primaryKey(),
});

// Oplog durável (o SyncMessageStore por trás de lib/sync/store.ts): uma linha por mensagem,
// identidade = (tabela, linha, coluna, HLC) — o messageKey. Persiste as mensagens LOCAIS
// drenadas da outbox E as REMOTAS recebidas dos peers, pra materializar/recompactar entre
// boots. Interno: NÃO sincroniza (o oplog viaja como arquivo cifrado no Drive, não como linha)
// e NÃO entra no backup .kontto (migração 0021, ver docs/features/SYNC.md § 3).
export const syncMessages = sqliteTable('sync_messages', {
    tableName: text('table_name').notNull(),
    rowId: text('row_id').notNull(),
    columnName: text('column_name').notNull(),
    timestamp: text('timestamp').notNull(),
    value: blob('value'),
    // 1 = mensagem local drenada mas AINDA NÃO confirmada no Drive (migração 0025). O ciclo
    // de sync sobe tudo que está pendente e só zera a flag após o upload dar certo — uma
    // falha de rede re-tenta no próximo ciclo com os MESMOS HLCs (sem re-carimbar), em vez
    // de perder o lote pra sempre (a outbox já foi esvaziada no dreno).
    pendingUpload: integer('pending_upload').notNull().default(0),
}, (table) => [
    primaryKey({ columns: [table.tableName, table.rowId, table.columnName, table.timestamp] }),
    index('idx_sync_messages_cell').on(table.tableName, table.rowId, table.columnName),
    index('idx_sync_messages_pending').on(table.pendingUpload),
]);

// Estado do sync em k/v: deviceId estável (nó do HLC), relógio HLC persistido (millis/counter)
// e cursores por device. Interno, não sincroniza, não entra no backup.
export const syncState = sqliteTable('sync_state', {
    key: text('key').primaryKey(),
    value: text('value'),
});

// ─── 17. Health Analyses (histórico de Saúde Financeira) ────

export const healthAnalyses = sqliteTable('health_analyses', {
    id: text('id').primaryKey().$defaultFn(generateId),
    // Período analisado, no formato yyyy-MM. Único por período (upsert).
    period: text('period').notNull(),
    score: integer('score').notNull(), // 0–100
    // Decomposição em sub-scores, serializada como JSON (HealthSubScore[]).
    subScores: text('sub_scores').notNull(),
    analysis: text('analysis').notNull(),
    // Arrays de strings serializados como JSON.
    strengths: text('strengths').notNull(),
    improvements: text('improvements').notNull(),
    source: text('source').notNull().default('local'), // ai, local

    createdAt: text('created_at').notNull().$defaultFn(() => new Date().toISOString()),
    updatedAt: text('updated_at').notNull().$defaultFn(() => new Date().toISOString()),
}, (table) => [
    uniqueIndex('idx_health_analyses_period').on(table.period),
    index('idx_health_analyses_created').on(table.createdAt),
]);
